import { differenceInCalendarDays, format } from 'date-fns'
import { Link } from 'react-router-dom'

import { Card } from '@/components/ui/card'
import type { FormField } from '@/types/api/form'
import type { RecordListItem } from '@/types/api/record'

import { parseDay, rawValue, recordHref, recordTitle } from './viewUtils'

/** One bar per record from its start to its end date (a single day when there is no end), on a shared axis. */
export function TimelineView({ records, startField, endField }: {
  records: RecordListItem[]
  startField: FormField | undefined
  endField: FormField | undefined
}) {
  if (!startField) {
    return <Card className="p-8 text-center text-sm text-muted-foreground shadow-none">Pick a start Date field for this timeline.</Card>
  }
  const rows = records.flatMap((record) => {
    const start = parseDay(rawValue(record, startField.key))
    if (!start) return []
    const end = parseDay(rawValue(record, endField?.key)) ?? start
    return [{ record, start, end: end < start ? start : end }]
  }).sort((a, b) => a.start.getTime() - b.start.getTime())
  if (rows.length === 0) {
    return <Card className="p-8 text-center text-sm text-muted-foreground shadow-none">No records have a date in this field.</Card>
  }
  const min = rows[0].start
  const max = rows.reduce((latest, r) => (r.end > latest ? r.end : latest), rows[0].end)
  const span = Math.max(differenceInCalendarDays(max, min) + 1, 1)

  return (
    <Card className="gap-0 overflow-hidden shadow-none">
      <div className="flex justify-between border-b border-border px-4 py-2 text-xs text-muted-foreground">
        <span>{format(min, 'dd/MM/yyyy')}</span>
        <span>{format(max, 'dd/MM/yyyy')}</span>
      </div>
      <div className="divide-y divide-border">
        {rows.map(({ record, start, end }) => (
          <div key={record.recordId} className="grid grid-cols-[10rem_minmax(0,1fr)] items-center gap-3 px-4 py-2">
            <Link to={recordHref(record)} className="truncate text-sm font-medium text-foreground hover:underline">{recordTitle(record)}</Link>
            <div className="relative h-5">
              <div
                className="absolute inset-y-0.5 rounded bg-primary/70"
                style={{
                  left: `${(differenceInCalendarDays(start, min) / span) * 100}%`,
                  width: `${Math.max(((differenceInCalendarDays(end, start) + 1) / span) * 100, 1)}%`,
                }}
                title={`${format(start, 'dd/MM/yyyy')} – ${format(end, 'dd/MM/yyyy')}`}
              />
            </div>
          </div>
        ))}
      </div>
    </Card>
  )
}
